import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { red, grey } from '@mui/material/colors';
import { Button, Container, Dialog, FormControl, Typography, TextField, Grid, CircularProgress } from '@mui/material';
import { updateUser } from '../../../api/userApi';
import { notification } from '../../../utils/Notifications';
import useLogout from '../../../hooks/auth/authHook.js';
import { usePermissions } from '../../../hooks/permissions/usePermissions.js';

UsersInfoFormDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
};

export default function UsersInfoFormDialog({ onClose, open, user, fetchUsers }) {
  const [logout] = useLogout();
  const { hasAllPermissions } = usePermissions();

  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    if (open) {
      
      setName(user.name || '');
      setMiddleName(user.paternalLastname || '');
      setLastName(user.maternalLastname || '');
      setPhoneNumber(user.phoneNumber || '');
      setEmail(user.email || '');
      setErrors({})
      
      if (user.role === "Sucursal") {
        setCanEdit(hasAllPermissions(["usr_eg"]));
        setIsFormValid(true);
      } else if (user.role === "Administrador") {
        setCanEdit(hasAllPermissions(["usr_ea"]));
        setIsFormValid(true);
      } else if (user.role === "Repartidor") {
        setCanEdit(hasAllPermissions(["usr_ra"]));
        setIsFormValid(true);
        setCanEdit(true);
      } else {
        setCanEdit(false);
      }
    } else {
      clearFields();
    }
  }, [open, user]);

  const handleNameChange = (event) => setName(event.target.value);
  const handleMiddleNameChange = (event) => setMiddleName(event.target.value);
  const handleLastNameChange = (event) => setLastName(event.target.value);
  const handlePhoneChange = (event) => {
    setPhoneNumber(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      phoneNumber: !validatePhoneNumber(event.target.value) ? 'El teléfono debe tener 10 números' : '',
    }));
  };
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      email: !validateEmail(event.target.value) ? 'Correo electrónico no válido' : '',
    }));
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhoneNumber = (phoneNumber) => {
    const regex = /^\d{10}$/;
    return regex.test(phoneNumber);
  };

  useEffect(() => {
    const validateForm = () => {
      const isValid = name && middleName && lastName && phoneNumber && email;
      const hasNoErrors = !errors.email && !errors.phoneNumber;
      setIsFormValid(isValid && hasNoErrors);
    };

    validateForm();
  }, [name, middleName, lastName, phoneNumber, email, errors]);

  const clearFields = () => {
    setName('');
    setMiddleName('');
    setLastName('');
    setPhoneNumber('');
    setEmail('');
    setErrors({});
  };

  const handleSaveAdminClick = () => {
    updateUserInfo();
    onClose();
  };

  const handleCloseAdminClick = () => {
    clearFields();
    handleDialogClose();
  };

  const buttonStyles = {
    width: '120px',
    borderRadius: '0.5rem',
    height: '38px',
  };

  const activeButtonStyles = {
    ...buttonStyles,
    backgroundColor: red['A400'],
    color: 'white',
    '&:hover': {
      backgroundColor: red['A700'],
    },
  };

  const inactiveButtonStyles = {
    ...buttonStyles,
    backgroundColor: grey[500],
    color: grey[300],
  };

  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const updateUserInfo = () => {

    if(!isFormValid) { return; }
    if(isLoading) { return; }
    setIsLoading(true);

    updateUser(user.id, email, phoneNumber, name, middleName, lastName,)
      .then((response) => {
        if (response.data.status_Code === 200) {
          notification({ text: "Usuario actualizado", type: "success" });
          fetchUsers();
          handleDialogClose();
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: response.data.status_Message || "Error al actualizar la información del usuario",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({text: 'Error de conexión, revisa tu conexión a internet', type: 'error'});
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <Dialog onClose={handleDialogClose} open={open} PaperProps={{ style: { borderRadius: '16px' } }}>
      <Container style={{ padding: 26, width: '600px' }}>
        <Typography component="div" variant="h5" style={{ textAlign: 'center', fontWeight: 'bold', color: grey[800], marginBottom: 26 }}>
          Información del usuario
        </Typography>

        <FormControl variant="standard">
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <TextField
                id="standard-basic"
                label="Nombre"
                variant="standard"
                value={name}
                onChange={handleNameChange}
                style={{ marginTop: '12px' }}
                error={Boolean(errors.name)}
                helperText={errors.name}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="standard-basic"
                label="Apellido paterno"
                variant="standard"
                value={middleName}
                onChange={handleMiddleNameChange}
                style={{ marginTop: '12px' }}
                error={Boolean(errors.middleName)}
                helperText={errors.middleName}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="standard-basic"
                label="Apellido materno"
                variant="standard"
                value={lastName}
                onChange={handleLastNameChange}
                style={{ marginTop: '12px' }}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="standard-basic"
                label="Teléfono"
                variant="standard"
                value={phoneNumber}
                onChange={handlePhoneChange}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="standard-basic"
                label="Correo electrónico"
                variant="standard"
                value={email}
                onChange={handleEmailChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                disabled={!canEdit}
              />
            </Grid>
          </Grid>
        </FormControl>

        <Container style={{ display: 'flex', justifyContent: 'end', width: '100%', padding: 0, marginTop: 36, gap: 12 }}>
          <Button
            variant="standard"
            color="primary"
            onClick={handleCloseAdminClick}
            style={{ width: '120px', borderRadius: '0.5rem', height: '38px', color: red['A400'], backgroundColor: red['50'] }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid || !canEdit}
            onClick={handleSaveAdminClick}
            sx={isFormValid && canEdit ? activeButtonStyles : inactiveButtonStyles}
          >
            {isLoading ? <CircularProgress size={24} /> : `Guardar`}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}
